import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building2, Edit, Lock, Bell, Linkedin, Chrome, CreditCard, Calendar, TrendingUp, Bookmark, Clock, MessageSquare, LogOut, Trash2, CheckCircle, XCircle, AlertCircle, Save, X } from 'lucide-react';
import axios from 'axios';
import MainFooter from '../Common/MainFooter';
import './ProfilePage.css';

const ProfilePage = () => {
  // User Profile State
  const [profile, setProfile] = useState({
    loginId: null,
    name: 'Sarah Anderson',
    email: 'sarah.anderson@example.com',
    phone: '+1 (555) 123-4567',
    userType: 'Organisation',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah'
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ ...profile });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Subscription State
  const [subscription, setSubscription] = useState({
    plan: 'Professional Plan',
    billingDate: 'December 25, 2024',
    features: ['Unlimited Keywords', 'Advanced Analytics', 'Priority Support']
  });

  // Demo Requests State
  const [demoRequests, setDemoRequests] = useState([
    { id: 1, product: 'Keyword Research Pro', date: '2024-11-20', status: 'approved' },
    { id: 2, product: 'Backlink Analyzer', date: '2024-11-18', status: 'pending' },
    { id: 3, product: 'Rank Tracker Elite', date: '2024-11-15', status: 'rejected' },
    { id: 4, product: 'Content Optimizer', date: '2024-11-10', status: 'approved' }
  ]);

  // Saved Products State
  const [savedProducts, setSavedProducts] = useState([
    { id: 1, name: 'SEO Audit Tool', category: 'Technical SEO', saved: true },
    { id: 2, name: 'Competitor Analysis', category: 'Research', saved: true },
    { id: 3, name: 'Link Building Suite', category: 'Off-Page SEO', saved: true },
    { id: 4, name: 'Content Planner', category: 'Content Marketing', saved: true }
  ]);

  // Activities State
  const [activities, setActivities] = useState([
    { id: 1, action: 'Profile Updated', time: '2 hours ago', icon: 'User' },
    { id: 2, action: 'Logged in from new device', time: '1 day ago', icon: 'Lock' },
    { id: 3, action: 'Demo requested for Keyword Tool', time: '3 days ago', icon: 'MessageSquare' },
    { id: 4, action: 'Subscription renewed', time: '5 days ago', icon: 'CreditCard' }
  ]);

  // Notifications State
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: true
  });

  // Connected Accounts State
  const [connectedAccounts, setConnectedAccounts] = useState({
    google: true,
    linkedin: true
  });

  // Modal States
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Password Change States
  const [passwordForm, setPasswordForm] = useState({
    username: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStep, setPasswordStep] = useState('request'); // 'request', 'verify', 'change'
  const [otpSent, setOtpSent] = useState(false);

  
  const API_BASE_URL = 'http://localhost:8082/api/seo-user';

 
  useEffect(() => {
  let isMounted = true;
  
  const loadUserData = async () => {
    try {
      const storedUserData = localStorage.getItem('userData');
      const storedLoginId = localStorage.getItem('loginId');
      const token = localStorage.getItem('token'); // ✅ Check for token

      // ✅ Only redirect if NO authentication data exists at all
      if (!storedUserData && !storedLoginId && !token) {
        console.warn('No user session found - redirecting to login');
        if (isMounted) {
          localStorage.clear();
          window.location.href = '/login';
        }
        return;
      }

      // ✅ If we have userData, use it immediately (no API call needed)
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          const profileData = {
            loginId: userData.loginId || storedLoginId,
            name: userData.name || 'User',
            email: userData.email || '',
            phone: userData.phone || '',
            userType: userData.userType || 'Individual',
            avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.name || 'User'}`
          };
          
          if (isMounted) {
            setProfile(profileData);
            setEditForm(profileData);
          }
          return; // ✅ Exit here - don't make API call if we have data
        } catch (parseError) {
          console.error('Error parsing stored user data:', parseError);
          // Continue to API fetch if parse fails
        }
      }

      // ✅ Only fetch from API if we have loginId but no complete userData
      if (storedLoginId && !storedUserData) {
        console.log('Fetching user profile from API...');
        if (isMounted) {
          setLoading(true);
        }
        
        try {
          const response = await axios.get(`${API_BASE_URL}/${storedLoginId}`, {
            timeout: 10000
          });
          
          if (response.status === 200 && response.data && isMounted) {
            const userData = response.data;
            const profileData = {
              loginId: userData.loginId,
              name: userData.name || 'User',
              email: userData.email || '',
              phone: userData.phone || '',
              userType: userData.userType || 'Individual',
              avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.name || 'User'}`
            };
            
            setProfile(profileData);
            setEditForm(profileData);
            localStorage.setItem('userData', JSON.stringify(profileData));
          }
        } catch (apiError) {
          console.error('Error fetching user profile:', apiError);
          
          // ✅ Only redirect on auth errors (401/403), not network errors
          if (apiError.response?.status === 401 || apiError.response?.status === 403) {
            if (isMounted) {
              console.warn('Authentication failed - redirecting to login');
              localStorage.clear();
              window.location.href = '/login';
            }
          } else {
            // ✅ For network errors, just show error but don't logout
            console.error('Network error loading profile, using cached data if available');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    } catch (error) {
      console.error('Unexpected error in loadUserData:', error);
      // ✅ Don't redirect on unexpected errors
    }
  };

  loadUserData();
  
  return () => {
    isMounted = false;
  };
}, []); 

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Icon mapping helper
  const getIcon = (iconName) => {
    const icons = {
      User: User,
      Lock: Lock,
      MessageSquare: MessageSquare,
      CreditCard: CreditCard
    };
    const IconComponent = icons[iconName] || Clock;
    return <IconComponent className="activity-icon" />;
  };

  // Handle Edit Profile
  const handleEditProfile = () => {
    setIsEditing(true);
    setEditForm({ ...profile });
  };

// Handle Save Profile - Integrated with Backend
const handleSaveProfile = async () => {
  // Validate required fields before sending
  if (!editForm.name?.trim()) {
    showNotification('Name is required', 'error');
    return;
  }
  
  if (!editForm.email?.trim()) {
    showNotification('Email is required', 'error');
    return;
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(editForm.email)) {
    showNotification('Please enter a valid email address', 'error');
    return;
  }

  // Validate phone format (optional but if provided must be valid)
  if (editForm.phone?.trim()) {
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(editForm.phone.trim())) {
      showNotification('Please enter a valid phone number', 'error');
      return;
    }
  }

  // Check if loginId exists
  if (!profile.loginId) {
    showNotification('User session invalid. Please login again.', 'error');
    setTimeout(() => {
      localStorage.clear();
      window.location.href = '/login';
    }, 2000);
    return;
  }

  setLoading(true);
  
  try {
    // Construct complete payload with all fields the backend expects
    const payload = {
      loginId: profile.loginId,                                   
      name: editForm.name.trim(),                                  
      email: editForm.email.trim(),                                
      phone: editForm.phone?.trim() || "",                         
      country: editForm.country?.trim() || "",                     
      state: editForm.state?.trim() || "",                        
      city: editForm.city?.trim() || "",                           
      pincode: editForm.pincode?.trim() || "",                     
      permanentAddress: editForm.permanentAddress?.trim() || "",   
      currentAddress: editForm.currentAddress?.trim() || "",       
      userType: editForm.userType || profile.userType || "Individual", 
      actionType: "UPDATE"                                        
    };

    console.log('Sending profile update payload:', payload);

    // Make API call to backend
    const response = await axios.post('http://localhost:8082/api/seo-user', payload, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 10000 // 10 second timeout
    });

    // Handle successful response
    if (response.status === 200 || response.status === 201) {
      // Update profile state with saved data
      const updatedProfile = {
        ...profile,
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone?.trim() || profile.phone,
        userType: editForm.userType || profile.userType,
        loginId: response.data.loginId || profile.loginId
      };
      
      setProfile(updatedProfile);
      
      // Update localStorage with new data
      localStorage.setItem('userData', JSON.stringify(updatedProfile));
      localStorage.setItem('loginId', updatedProfile.loginId);
      
      // Exit edit mode
      setIsEditing(false);
      
      // Log activity
      addActivity('Profile Updated', 'just now');
      
      // Show success notification
      showNotification('Profile updated successfully!', 'success');
    }
  } catch (error) {
    console.error('Error updating profile:', error);
    console.error('Error response:', error.response?.data);

    // Handle specific backend error responses
    if (error.response) {
      // Server responded with error status
      const errorTitle = error.response.data?.title?.toLowerCase();
      const errorMessage = error.response.data?.message;
      const errorStatus = error.response.status;
      
      switch (errorTitle) {
        case 'emailexists':
          showNotification('This email is already registered. Please use a different email.', 'error');
          break;
          
        case 'usernotfound':
          showNotification('User account not found. Please try logging in again.', 'error');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 3000);
          break;
          
        case 'invaliddata':
        case 'validation_error':
          showNotification(errorMessage || 'Invalid data provided. Please check your input.', 'error');
          break;
          
        case 'unauthorized':
          showNotification('Session expired. Please login again.', 'error');
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 2000);
          break;
          
        default:
          if (errorStatus === 400) {
            showNotification(
              errorMessage || 'Invalid request. Please check all fields and try again.',
              'error'
            );
          } else if (errorStatus === 401 || errorStatus === 403) {
            showNotification('Session expired. Please login again.', 'error');
            setTimeout(() => {
              localStorage.clear();
              window.location.href = '/login';
            }, 2000);
          } else {
            showNotification(
              errorMessage || `Failed to update profile (Error ${errorStatus}). Please try again.`,
              'error'
            );
          }
      }
    } else if (error.request) {
      // Request made but no response received (network error)
      showNotification('Network error. Please check your connection and try again.', 'error');
    } else {
      // Something else went wrong
      showNotification('An unexpected error occurred. Please try again.', 'error');
    }
    
    // Log detailed error for debugging
    console.error('Profile update error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers
    });
  } finally {
    setLoading(false);
  }
};


  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({ ...profile });
  };

  // Handle Password Change - Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/changePasswordOtp`,
        null,
        { params: { username: passwordForm.username } }
      );

      if (response.status === 200 || response.status === 201) {
        setOtpSent(true);
        setPasswordStep('verify');
        showNotification('OTP sent to your registered email!', 'success');
      }
    } catch (error) {
      console.error('Error requesting OTP:', error);
      if (error.response?.data?.title === 'usernotfound') {
        showNotification('User not found. Please check your username.', 'error');
      } else {
        showNotification('Failed to send OTP. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Password Change - Step 2: Validate OTP
  const handleValidateOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        userName: passwordForm.username,
        otp: passwordForm.otp
      };

      const response = await axios.post(`${API_BASE_URL}/validateOtp`, payload);

      if (response.status === 200 || response.status === 201) {
        setPasswordStep('change');
        showNotification('OTP verified successfully!', 'success');
      }
    } catch (error) {
      console.error('Error validating OTP:', error);
      showNotification('Invalid OTP. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle Password Change - Step 3: Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotification('Passwords do not match!', 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userName: passwordForm.username,
        newPassword: passwordForm.newPassword
      };

      const response = await axios.post(`${API_BASE_URL}/changePassword`, payload);

      if (response.status === 200 || response.status === 201) {
        setShowPasswordModal(false);
        setPasswordForm({ username: '', otp: '', newPassword: '', confirmPassword: '' });
        setPasswordStep('request');
        setOtpSent(false);
        addActivity('Password Changed', 'just now');
        showNotification('Password changed successfully!', 'success');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showNotification('Failed to change password. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Open Password Modal
  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordForm({ ...passwordForm, username: profile.email });
  };

  // Handle Notifications
  const toggleNotification = (type) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    addActivity(`${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${!notifications[type] ? 'enabled' : 'disabled'}`, 'just now');
  };

  // Handle Connected Accounts
  const toggleAccount = (account) => {
    setConnectedAccounts(prev => ({
      ...prev,
      [account]: !prev[account]
    }));
    const accountName = account.charAt(0).toUpperCase() + account.slice(1);
    addActivity(`${accountName} ${!connectedAccounts[account] ? 'connected' : 'disconnected'}`, 'just now');
  };

  // Handle Subscription Upgrade
  const handleUpgrade = () => {
    showNotification('Redirecting to upgrade page...', 'info');
    addActivity('Viewed upgrade options', 'just now');
  };

  // Handle Product Bookmark
  const toggleBookmark = (productId) => {
    setSavedProducts(prev =>
      prev.map(p => p.id === productId ? { ...p, saved: !p.saved } : p)
    );
  };

  const removeProduct = (productId) => {
    const product = savedProducts.find(p => p.id === productId);
    setSavedProducts(prev => prev.filter(p => p.id !== productId));
    addActivity(`Removed ${product.name} from saved products`, 'just now');
  };

  // Handle Support Ticket
  const handleRaiseTicket = () => {
    setShowTicketModal(true);
  };

  const handleTicketSubmit = (e) => {
    e.preventDefault();
    setShowTicketModal(false);
    addActivity('Support ticket raised', 'just now');
    showNotification('Ticket submitted successfully! Our team will contact you soon.', 'success');
  };

  const handleViewTickets = () => {
    showNotification('Opening ticket history...', 'info');
    addActivity('Viewed ticket history', 'just now');
  };

  // Handle Account Deletion - Integrated with Backend
  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = async () => {
    setLoading(true);

    try {
      const payload = {
        loginId: profile.loginId,
        actionType: 'DELETE'
      };

      const response = await axios.post(API_BASE_URL, payload);

      if (response.status === 204 || response.status === 200) {
        setShowDeleteModal(false);
        addActivity('Account deletion requested', 'just now');
        showNotification('Account deletion process initiated. You will receive a confirmation email.', 'success');
        // Redirect to login or home page after a delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      showNotification('Failed to delete account. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    addActivity('Logged out', 'just now');
    setShowLogoutModal(false);
    showNotification('Logging out...', 'info');
    // Clear local storage and redirect to login
    setTimeout(() => {
      localStorage.clear();
      window.location.href = '/login';
    }, 1500);
  };

  // Add Activity Helper
  const addActivity = (action, time) => {
    const newActivity = {
      id: Date.now(),
      action,
      time,
      icon: 'User'
    };
    setActivities(prev => [newActivity, ...prev].slice(0, 10));
  };

  // Status Badge Helper
  const getStatusBadge = (status) => {
    return `status-badge status-${status}`;
  };

  const getStatusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="status-icon" />;
    if (status === 'rejected') return <XCircle className="status-icon" />;
    return <AlertCircle className="status-icon" />;
  };

  return (
    <div className="profile-page">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`notification-toast ${notification.type === 'success' ? 'toast-success' :
            notification.type === 'error' ? 'toast-error' : 'toast-info'
          }`}>
          {notification.message}
        </div>
      )}

      <div className="profile-container">
        <h1 className="page-title">My Profile</h1>

        {/* Profile Header */}
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              <img src={profile.avatar} alt="Profile" className="profile-avatar" />
              <div className="online-indicator"></div>
            </div>

            {!isEditing ? (
              <div className="profile-info">
                <h2 className="profile-name">{profile.name}</h2>
                <div className="profile-details">
                  <div className="profile-detail-item">
                    <Mail className="detail-icon" />
                    <span>{profile.email}</span>
                  </div>
                  <div className="profile-detail-item">
                    <Phone className="detail-icon" />
                    <span>{profile.phone}</span>
                  </div>
                  <div className="profile-detail-item">
                    <Building2 className="detail-icon" />
                    <span className="user-type-badge">{profile.userType}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="profile-edit-form">
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="edit-input"
                  placeholder="Full Name"
                />
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="edit-input"
                  placeholder="Email"
                />
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="edit-input"
                  placeholder="Phone"
                />
                <select
                  value={editForm.userType}
                  onChange={(e) => setEditForm({ ...editForm, userType: e.target.value })}
                  className="edit-input"
                >
                  <option value="Individual">Individual</option>
                  <option value="Organisation">Organisation</option>
                </select>
              </div>
            )}

            <div className="profile-actions">
              {!isEditing ? (
                <button onClick={handleEditProfile} className="btn-primary">
                  <Edit className="btn-icon" />
                  Edit Profile
                </button>
              ) : (
                <>
                  <button onClick={handleSaveProfile} disabled={loading} className="btn-success">
                    <Save className="btn-icon" />
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancelEdit} className="btn-secondary">
                    <X className="btn-icon" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Account Settings */}
        <div className="profile-card">
          <h3 className="section-title">Account Settings</h3>
          <div className="settings-grid">
            <div className="setting-card" onClick={openPasswordModal}>
              <div className="setting-header">
                <div className="setting-icon-wrapper blue">
                  <Lock className="setting-icon" />
                </div>
                <h4 className="setting-title">Change Password</h4>
              </div>
              <p className="setting-description">Update your password regularly for security</p>
              <button className="setting-link">Update Password →</button>
            </div>

            <div className="setting-card">
              <div className="setting-header">
                <div className="setting-icon-wrapper purple">
                  <Bell className="setting-icon" />
                </div>
                <h4 className="setting-title">Notifications</h4>
              </div>
              <div className="notification-toggles">
                {Object.entries(notifications).map(([key, value]) => (
                  <div key={key} className="toggle-item">
                    <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => toggleNotification(key)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="setting-card connected-accounts">
              <div className="setting-header">
                <h4 className="setting-title">Connected Accounts</h4>
              </div>
              <div className="account-badges">
                <div
                  className={`account-badge ${connectedAccounts.google ? 'connected' : ''}`}
                  onClick={() => toggleAccount('google')}
                >
                  <Chrome className="account-icon" />
                  <span>Google</span>
                  <span className="connection-status">
                    {connectedAccounts.google ? 'Connected' : 'Connect'}
                  </span>
                </div>
                <div
                  className={`account-badge ${connectedAccounts.linkedin ? 'connected' : ''}`}
                  onClick={() => toggleAccount('linkedin')}
                >
                  <Linkedin className="account-icon linkedin" />
                  <span>LinkedIn</span>
                  <span className="connection-status">
                    {connectedAccounts.linkedin ? 'Connected' : 'Connect'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="profile-card">
          <h3 className="section-title">Subscription & Plan</h3>
          <div className="subscription-card">
            <div className="subscription-content">
              <div className="subscription-info">
                <div className="subscription-header">
                  <CreditCard className="subscription-icon" />
                  <h4 className="subscription-plan">{subscription.plan}</h4>
                </div>
                <div className="billing-info">
                  <Calendar className="billing-icon" />
                  <span>Next billing: {subscription.billingDate}</span>
                </div>
                <div className="feature-tags">
                  {subscription.features.map((feature, idx) => (
                    <span key={idx} className="feature-tag">{feature}</span>
                  ))}
                </div>
              </div>
              <button onClick={handleUpgrade} className="btn-upgrade">
                <TrendingUp className="btn-icon" />
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>

        {/* Demo Requests */}
        <div className="profile-card">
          <h3 className="section-title">Demo Request History</h3>
          <div className="table-wrapper">
            <table className="demo-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {demoRequests.map((request) => (
                  <tr key={request.id}>
                    <td className="product-name">{request.product}</td>
                    <td className="request-date">{request.date}</td>
                    <td>
                      <span className={getStatusBadge(request.status)}>
                        {getStatusIcon(request.status)}
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Saved Products */}
        <div className="profile-card">
          <h3 className="section-title">Saved Products</h3>
          <div className="products-grid">
            {savedProducts.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-header">
                  <div className="product-icon-wrapper">
                    <TrendingUp className="product-icon" />
                  </div>
                  <button onClick={() => removeProduct(product.id)} className="bookmark-btn">
                    <Bookmark className={`bookmark-icon ${product.saved ? 'filled' : ''}`} />
                  </button>
                </div>
                <h4 className="product-name">{product.name}</h4>
                <p className="product-category">{product.category}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Log */}
        <div className="profile-card">
          <h3 className="section-title">Activity Log</h3>
          <div className="activity-list">
            {activities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon-wrapper">
                  {getIcon(activity.icon)}
                </div>
                <div className="activity-content">
                  <p className="activity-action">{activity.action}</p>
                  <div className="activity-time">
                    <Clock className="time-icon" />
                    <span>{activity.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Support */}
        <div className="profile-card">
          <h3 className="section-title">Support</h3>
          <div className="support-grid">
            <button className="support-card" onClick={handleRaiseTicket}>
              <MessageSquare className="support-icon green" />
              <div className="support-text">
                <p className="support-title">Raise a Ticket</p>
                <p className="support-description">Get help from our support team</p>
              </div>
            </button>
            <button className="support-card" onClick={handleViewTickets}>
              <Clock className="support-icon blue" />
              <div className="support-text">
                <p className="support-title">View Ticket History</p>
                <p className="support-description">Check your past support tickets</p>
              </div>
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="profile-card danger-zone">
          <h3 className="section-title danger">Danger Zone</h3>
          <div className="danger-actions">
            <button className="btn-danger" onClick={handleDeleteAccount}>
              <Trash2 className="btn-icon" />
              Delete Account
            </button>
            <button className="btn-logout" onClick={handleLogout}>
              <LogOut className="btn-icon" />
              Logout
            </button>
          </div>
        </div>

        {/* Modals */}
        {showPasswordModal && (
          <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Change Password</h3>

              {passwordStep === 'request' && (
                <form onSubmit={handleRequestOTP}>
                  <input
                    type="text"
                    value={passwordForm.username}
                    onChange={(e) => setPasswordForm({ ...passwordForm, username: e.target.value })}
                    placeholder="Username/Email"
                    required
                    className="modal-input"
                  />
                  <div className="modal-actions">
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Sending...' : 'Send OTP'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {passwordStep === 'verify' && (
                <form onSubmit={handleValidateOTP}>
                  <input
                    type="text"
                    value={passwordForm.otp}
                    onChange={(e) => setPasswordForm({ ...passwordForm, otp: e.target.value })}
                    placeholder="Enter OTP"
                    required
                    className="modal-input"
                  />
                  <div className="modal-actions">
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {passwordStep === 'change' && (
                <form onSubmit={handleChangePassword}>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="New Password"
                    required
                    className="modal-input"
                  />
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Confirm New Password"
                    required
                    className="modal-input"
                  />
                  <div className="modal-actions">
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Changing...' : 'Change Password'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {showTicketModal && (
          <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Raise Support Ticket</h3>
              <form onSubmit={handleTicketSubmit}>
                <input type="text" placeholder="Subject" required className="modal-input" />
                <textarea placeholder="Describe your issue..." required className="modal-textarea"></textarea>
                <select className="modal-input" required>
                  <option value="">Select Priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <div className="modal-actions">
                  <button type="submit" className="btn-primary">Submit Ticket</button>
                  <button type="button" className="btn-secondary" onClick={() => setShowTicketModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Account</h3>
              <p className="modal-warning">Are you sure you want to delete your account? This action cannot be undone.</p>
              <div className="modal-actions">
                <button className="btn-danger" onClick={confirmDeleteAccount} disabled={loading}>
                  {loading ? 'Deleting...' : 'Yes, Delete My Account'}
                </button>
                <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showLogoutModal && (
          <div className="modal-overlay" onClick={() => setShowLogoutModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Logout</h3>
              <p className="modal-warning" style={{ background: '#fef3c7', color: '#92400e' }}>Are you sure you want to logout?</p>
              <div className="modal-actions">
                <button className="btn-logout" onClick={confirmLogout}>
                  Yes, Logout
                </button>
                <button className="btn-secondary" onClick={() => setShowLogoutModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <MainFooter />
    </div>

  );
};

export default ProfilePage;